/**
 * Colore del giocatore/pezzo
 * WHITE muove per primo e parte dal basso (righe 1-4)
 * BLACK parte dall'alto (righe 7-10)
 */
export enum Color {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
}

/**
 * Tipo di pezzo
 * MAN = pedina normale, si muove solo in avanti
 * KING = dama, si muove in tutte le direzioni diagonali
 */
export enum PieceType {
  MAN = 'MAN',
  KING = 'KING',
}

/**
 * Stato della partita
 */
export enum GameStatus {
  WAITING = 'WAITING',       // In attesa di avversario
  PLAYING = 'PLAYING',       // Partita in corso
  WHITE_WINS = 'WHITE_WINS', // Bianco vince
  BLACK_WINS = 'BLACK_WINS', // Nero vince
  DRAW = 'DRAW',             // Patta
  ABANDONED = 'ABANDONED',   // Abbandonata
}

/**
 * Variante di gioco. Allineata con la Master Matrix in /variants/variants.jsonc
 * e con `enum class VariantType` in packages/engine/include/draughts/core/types.hpp.
 *
 * I valori INTERNATIONAL/ITALIAN/ENGLISH/BRAZILIAN sono le uniche varianti
 * runtime-attive lato server (vedi ACTIVE_VARIANTS). Le altre sono dichiarate
 * per consentire alla matrice di esprimerle (UI, validazione, codegen C++)
 * ma non sono ancora abilitate come scelte di partita.
 */
export enum GameVariant {
  INTERNATIONAL = 'INTERNATIONAL', // 10x10, flying kings
  ITALIAN = 'ITALIAN',             // 8x8, no flying kings, king priority
  ENGLISH = 'ENGLISH',             // 8x8, American checkers
  BRAZILIAN = 'BRAZILIAN',         // 8x8, International-like
  FRISIAN = 'FRISIAN',             // 10x10, catture mixed, king vale 3
  FRYSK = 'FRYSK',                 // Frysk! 5 pedine — famiglia frisone
  TURKISH = 'TURKISH',             // 8x8 con tutte le caselle, ortogonale
  ARMENIAN = 'ARMENIAN',           // Tama — famiglia ortogonale turca
  RUSSIAN = 'RUSSIAN',             // 8x8, immediate promotion mid-capture
  SPANISH = 'SPANISH',             // 8x8, flying kings, men forward only
  TRIESTINE = 'TRIESTINE',         // 10x10, no flying king, king priority
  CANADIAN = 'CANADIAN',           // 12x12, International-like
  NIGERIAN = 'NIGERIAN',           // 10x10, FMJD-like ma free-choice capture
  GHANAIAN = 'GHANAIAN',           // 10x10, Nigerian + single-piece-loses
  HEX = 'HEX',                     // sperimentale, board esagonale
  POOL = 'POOL',                   // sperimentale, Pool Checkers USA
  CZECH = 'CZECH',                 // sperimentale, Česká dáma 8x8
  THAI = 'THAI',                   // sperimentale, Makhos 8 pedine
  PORTUGUESE = 'PORTUGUESE',       // sperimentale, Damas portuguesas
  FILIPINO = 'FILIPINO',           // sperimentale, Dama filippina
  MALAYSIAN = 'MALAYSIAN',         // sperimentale, Dam 12x12
  GERMAN = 'GERMAN',               // sperimentale, Damenspiel
  JAMAICAN = 'JAMAICAN',           // sperimentale, Pool + English
  SINHALESE = 'SINHALESE',         // sperimentale, Choka Sri Lanka
  SOUTH_AFRICAN = 'SOUTH_AFRICAN', // sperimentale, 14x14 35 pedine
  DAMEO = 'DAMEO',                 // sperimentale, Freeling 2000, ortogonale
  CRODA = 'CRODA',                 // sperimentale, Dedić, no dame
}

const STRING_TO_VARIANT: Record<string, GameVariant> = {
  INTERNATIONAL: GameVariant.INTERNATIONAL,
  ITALIAN: GameVariant.ITALIAN,
  ENGLISH: GameVariant.ENGLISH,
  BRAZILIAN: GameVariant.BRAZILIAN,
  FRISIAN: GameVariant.FRISIAN,
  FRYSK: GameVariant.FRYSK,
  TURKISH: GameVariant.TURKISH,
  ARMENIAN: GameVariant.ARMENIAN,
  RUSSIAN: GameVariant.RUSSIAN,
  SPANISH: GameVariant.SPANISH,
  TRIESTINE: GameVariant.TRIESTINE,
  CANADIAN: GameVariant.CANADIAN,
  NIGERIAN: GameVariant.NIGERIAN,
  GHANAIAN: GameVariant.GHANAIAN,
  HEX: GameVariant.HEX,
  POOL: GameVariant.POOL,
  CZECH: GameVariant.CZECH,
  THAI: GameVariant.THAI,
  PORTUGUESE: GameVariant.PORTUGUESE,
  FILIPINO: GameVariant.FILIPINO,
  MALAYSIAN: GameVariant.MALAYSIAN,
  GERMAN: GameVariant.GERMAN,
  JAMAICAN: GameVariant.JAMAICAN,
  SINHALESE: GameVariant.SINHALESE,
  SOUTH_AFRICAN: GameVariant.SOUTH_AFRICAN,
  DAMEO: GameVariant.DAMEO,
  CRODA: GameVariant.CRODA,
};

/**
 * Questa build conosce questa stringa?
 *
 * Diverso da `VALID_VARIANT_STRINGS`, che e' l'elenco delle varianti ATTIVE
 * (giocabili) in minuscolo: la domanda qui e' «so cosa significa», non «si puo'
 * giocare» — HEX sta nell'enum e fuori dalle attive. Serve a chi deve decidere
 * se FIDARSI di una stringa arrivata dal filo.
 */
export function isKnownVariant(str: string): boolean {
  // La stessa lettura di `toGameVariant`, non un `in`: le due devono rispondere
  // della stessa tabella, o una direbbe «la conosco» dove l'altra ripiega.
  return STRING_TO_VARIANT[str.toUpperCase()] !== undefined;
}

/*
 * Le stringhe sconosciute gia' segnalate. Un `Set` a livello di modulo perche'
 * `toGameVariant` viene chiamata A OGNI RENDER (l'intestazione della partita la
 * chiama, e l'orologio la ridisegna cinque volte al secondo): senza deduplica
 * sarebbero migliaia di righe al minuto, cioe' un errore che non si legge.
 */
const varianteIgnotaGiaSegnalata = new Set<string>();

/**
 * Convert any string to GameVariant enum value.
 *
 * Su una stringa che questa build non conosce ripiega su INTERNATIONAL — non
 * lancia: un dato di rete non merita una schermata bianca — ma lo DICE, una
 * volta per stringa. Il silenzio di prima e' quel che ha reso invisibile per
 * mesi la variante persa nella rivincita: un dato sbagliato non sembrava un
 * errore, sembrava una partita internazionale legittima.
 */
export function toGameVariant(str: string): GameVariant {
  const known = STRING_TO_VARIANT[str.toUpperCase()];
  if (known) return known;
  if (!varianteIgnotaGiaSegnalata.has(str)) {
    varianteIgnotaGiaSegnalata.add(str);
    /*
     * Via `globalThis` e non `console` nudo: questo pacchetto compila con
     * `lib: ["ES2022"]` e nient'altro — niente DOM, niente tipi di Node — e lo
     * importano browser, server e test. Cosi' l'errore si scrive dove c'e' una
     * console e tace dove non c'e', senza aprire il pacchetto puro ai globali di
     * un ambiente in particolare.
     */
    (globalThis as { console?: { error?: (...args: unknown[]) => void } }).console?.error?.(
      `[toGameVariant] variante sconosciuta: "${str}" — ripiego su INTERNATIONAL. ` +
      'Chi ha prodotto questa stringa sta mandando una variante che questa build non conosce.'
    );
  }
  return GameVariant.INTERNATIONAL;
}

/**
 * Convert GameVariant enum to lowercase string
 */
export function getVariantString(variant: GameVariant): string {
  return variant.toLowerCase();
}

/**
 * Currently active/playable variants — il server accetta solo queste in game:create
 * e la DB ha colonne rating per ognuna. Tutte le 26 varianti supportate dalla griglia
 * quadrata sono attive (migrations 005, 018, 029, 032-039, 040-053).
 * HEX (board esagonale) resta dichiarata nell'enum ma non giocabile finché la UI
 * e l'engine non supportano hex shape.
 *
 * ⚠️ L'ORDINE di questo array è l'ordine canonico mostrato all'utente ovunque: griglia
 * delle regole, "altre varianti", tutti i dropdown di scelta variante, tab della
 * leaderboard, filtri arena/tornei, variante preferita nel profilo, chip della landing,
 * prerender e sitemap. Le varianti imparentate stanno accanto (Frisone/Frysk,
 * Turca/Armena). Se lo cambi, riallinea le liste gemelle scritte a mano: l'enum qui
 * sopra, STRING_TO_VARIANT, VARIANT_CONFIGS (constants/board.ts), la Master Matrix
 * (variants/variants.jsonc + variants.schema.json), VARIANT_SLUGS (web/src/rules/routes.ts),
 * il blocco game.variants.* dei 7 locale e le liste varianti del server.
 */
export const ACTIVE_VARIANTS: GameVariant[] = [
  GameVariant.INTERNATIONAL,
  GameVariant.ITALIAN,
  GameVariant.ENGLISH,
  GameVariant.BRAZILIAN,
  GameVariant.FRISIAN,
  GameVariant.FRYSK,
  GameVariant.TURKISH,
  GameVariant.ARMENIAN,
  GameVariant.RUSSIAN,
  GameVariant.SPANISH,
  GameVariant.TRIESTINE,
  GameVariant.CANADIAN,
  GameVariant.NIGERIAN,
  GameVariant.GHANAIAN,
  GameVariant.POOL,
  GameVariant.CZECH,
  GameVariant.THAI,
  GameVariant.PORTUGUESE,
  GameVariant.FILIPINO,
  GameVariant.MALAYSIAN,
  GameVariant.GERMAN,
  GameVariant.JAMAICAN,
  GameVariant.SINHALESE,
  GameVariant.SOUTH_AFRICAN,
  // TEMP: Dameo e Croda nascoste (possibile copyright su nome/regole). Restano
  // dichiarate nell'enum + Master Matrix ma NON attive → invisibili in UI, escluse
  // da matchmaking/bot/regole/preferenze e rifiutate dal server in game:create.
  // Per riabilitarle: ripristina le due righe qui sotto.
  // GameVariant.DAMEO,
  // GameVariant.CRODA,
];

/**
 * Lowercase strings for active variants (for validation)
 */
export const VALID_VARIANT_STRINGS: string[] = ACTIVE_VARIANTS.map(v => getVariantString(v));

/**
 * Finestre temporali offerte dalla classifica «più attivi». `null` = di sempre.
 *
 * Sta qui, e non nei due pacchetti che la usano, perché i due usi devono
 * combaciare: il server la tratta come ALLOW-LIST — è un endpoint pubblico, e
 * senza un elenco chiuso ogni valore di `days` aprirebbe una chiave Redis nuova
 * — e il client ci disegna sopra il selettore. Stesso motivo di
 * TOLLERANZA_OROLOGIO_MS.
 */
export const MOST_ACTIVE_PERIOD_DAYS = [7, 30, 90] as const;

/** Finestra predefinita: la card in home e l'atterraggio in Classifica. */
export const MOST_ACTIVE_DEFAULT_DAYS = 30;
